import { DuckSelector, objectDuckCreator } from '@atc/modular-redux';

import validateUserInput from '@/utilities/validateUserInput';
import { optimizeListingData, optimizeVehicleFeatures } from '@/utilities/vehicleSummaryUtils';

import ListingsAgentFetcher from '@/fetchers/ListingsAgentFetcher';

import userDuck from './userDuck';
import vdpModelInfoDuck from './vdp/vdpModelInfoDuck';
import vdpResultsDuck from './vdp/vdpResultsDuck';

function addListingContext(getState) {
    const listing = vdpResultsDuck.selectors.getVdpActiveInventory(getState());
    if (!listing) {
        return undefined;
    }

    const optimizedListing = optimizeListingData(listing);
    if (!optimizedListing) {
        return undefined;
    }

    const { features } = vdpModelInfoDuck.selectors.getModelInfo(getState()) || {};
    const optimizedFeatures = optimizeVehicleFeatures(features);
    const vehicleDetails = {
        listing: optimizedListing,
        additionalFeatures: optimizedFeatures,
    };

    return JSON.stringify(vehicleDetails);
}

// Helper function to build sessionContext with conditional attributes
function buildSessionContext(getState, listingsAgentDuck) {
    const state = getState();

    const listing = vdpResultsDuck.selectors.getVdpActiveInventory(state);
    const userZip = userDuck.selectors.getZip(state);
    const sessionId = listingsAgentDuck.selectors.getSessionId(state);
    const lastMessageTime = listingsAgentDuck.selectors.getLastMessageTime(state);

    const promptSessionAttributes = {};
    if (listing?.id) {
        promptSessionAttributes.currentListingId = `${listing.id}`;
    }
    if (userZip) {
        promptSessionAttributes.zip = userZip;
    }

    const sessionContext = {
        sessionState: {
            promptSessionAttributes,
        },
    };

    if (sessionId) {
        sessionContext.sessionId = sessionId;
    }

    if (lastMessageTime) {
        sessionContext.lastMessageTime = lastMessageTime;
    }

    return sessionContext;
}

// Helper function to handle errors consistently
function handleAgentError(error, dispatch, duck) {
    // eslint-disable-next-line no-console
    console.error('Error calling listings agent:', error);

    return dispatch(duck?.creators?.addMessage({
        message: 'Sorry, I encountered an error while processing your request. Please try again.',
        source: 'agent',
    }));
}

// Cached formatting strings to avoid string concatenation on every call
const FORMATTING_INSTRUCTIONS = {
    vehicleSummary: 'Format your response in the following way: [brief intro stating basic car info] [16px spacing] [bulleted list titled "Key Highlights"- include details about price, mileage, engine, and fuel economy] [16px spacing] [bulleted list titled "Key Features" - include a maximum of 5] [16px spacing] [two sentence summary describing what\'s great about the vehicle]. Format the response only with valid html.',
};

function addResponseFormatting({ type = 'general', message, getState }) {
    const vehicleContext = addListingContext(getState);

    if (type === 'vehicleSummary' && vehicleContext) {
        return `Provide a short summary of what's great about the following vehicle: ${vehicleContext}. ${FORMATTING_INSTRUCTIONS.vehicleSummary}`;
    }

    const baseMessage = vehicleContext
        ? `${message} - This may relate to the vehicle with the following details: ${vehicleContext}`
        : message;

    return `${baseMessage}`;
}

const listingsAgentDuck = objectDuckCreator({
    store: 'listingsAgent',
    initialState: {
        messages: [],
        isLoading: false,
        sessionId: null,
        lastMessageTime: null,
    },
}).extend({
    selectors: () => ({
        getMessages: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).messages),
        isLoading: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).isLoading),
        getSessionId: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).sessionId),
        getLastMessageTime: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).lastMessageTime),
        hasVehicleSummary: new DuckSelector((selectors) => (state, listingId) => {
            const { messages } = selectors.getDuckState(state);
            return messages.some((msg) => msg.type === 'vehicleSummary' && msg.listingId === listingId);
        }),
    }),
    creators: () => ({
        addMessage: ({ message, source, type, listingId }) => async (dispatch, getState) => {
            const currentMessages = listingsAgentDuck.selectors.getMessages(getState());
            const updatedMessages = [
                ...currentMessages,
                {
                    id: Date.now(),
                    source,
                    type,
                    content: message,
                    timestamp: new Date(),
                    ...(listingId ? { listingId } : {}),
                },
            ];

            await dispatch(listingsAgentDuck?.creators?.setKeys({ messages: updatedMessages }));
        },
        setLoading: (isLoading) => async (dispatch) => {
            await dispatch(listingsAgentDuck?.creators?.setKeys({ isLoading }));
        },
        clearMessages: () => async (dispatch) => {
            await dispatch(listingsAgentDuck?.creators?.setKeys({ messages: [] }));
        },
        sendUserMessage: (message) => async (dispatch, getState) => {
            // Validate the input message (empty check handled by component)
            const { isValid, cleanMessage } = validateUserInput(message);

            // Early return for invalid messages - show error
            if (!isValid) {
                return dispatch(listingsAgentDuck?.creators?.addMessage({
                    message: 'I\'m sorry, but your message appears to contain invalid content. Please try rephrasing your question.',
                    source: 'agent',
                }));
            }

            // Add user message to chat and set loading state
            await Promise.all([
                dispatch(listingsAgentDuck?.creators?.addMessage({ message: cleanMessage, source: 'user' })),
                dispatch(listingsAgentDuck?.creators?.setLoading(true)),
            ]);

            try {
                const response = await ListingsAgentFetcher({
                    message: addResponseFormatting({ message: cleanMessage, getState }),
                    sessionContext: buildSessionContext(getState, listingsAgentDuck),
                });

                // Store sessionId and lastMessageTime if provided in response
                const updatedKeys = {};
                if (response?.sessionContext?.sessionId) {
                    updatedKeys.sessionId = response.sessionContext.sessionId;
                }
                if (response?.sessionContext?.lastMessageTime) {
                    updatedKeys.lastMessageTime = response.sessionContext.lastMessageTime;
                }
                if (Object.keys(updatedKeys).length > 0) {
                    await dispatch(listingsAgentDuck?.creators?.setKeys(updatedKeys));
                }

                // Add agent response to chat
                await dispatch(listingsAgentDuck?.creators?.addMessage({
                    message: response?.response || 'Sorry, I couldn\'t process your request.',
                    source: 'agent',
                }));

            } catch (error) {
                await handleAgentError(error, dispatch, listingsAgentDuck);
            } finally {
                await dispatch(listingsAgentDuck?.creators?.setLoading(false));
            }

            return undefined;
        },
        sendVehicleSummaryMessage: () => async (dispatch, getState) => {
            const fallbackSummaryText = 'I apologize, but I\'m unable to retrieve the vehicle information.';
            const listing = vdpResultsDuck.selectors.getVdpActiveInventory(getState());

            // Add user message and set loading state in parallel
            await Promise.all([
                dispatch(listingsAgentDuck?.creators?.addMessage({
                    source: 'user',
                    message: 'What\'s great about this vehicle?',
                })),
                dispatch(listingsAgentDuck?.creators.setLoading(true)),
            ]);

            try {
                const response = await ListingsAgentFetcher({
                    message: addResponseFormatting({ type: 'vehicleSummary', message: '', getState }),
                    sessionContext: buildSessionContext(getState, listingsAgentDuck),
                });

                // Store sessionId and lastMessageTime if provided in response
                const updatedKeys = {};
                if (response?.sessionContext?.sessionId) {
                    updatedKeys.sessionId = response.sessionContext.sessionId;
                }
                if (response?.sessionContext?.lastMessageTime) {
                    updatedKeys.lastMessageTime = response.sessionContext.lastMessageTime;
                }
                if (Object.keys(updatedKeys).length > 0) {
                    await dispatch(listingsAgentDuck?.creators?.setKeys(updatedKeys));
                }

                await dispatch(listingsAgentDuck?.creators?.addMessage({
                    source: 'agent',
                    type: 'vehicleSummary',
                    listingId: listing?.id,
                    message: response?.response || fallbackSummaryText,
                }));
            } catch (error) {
                await handleAgentError(error, dispatch, listingsAgentDuck);
            } finally {
                await dispatch(listingsAgentDuck?.creators.setLoading(false));
            }
        },
    }),
});

export default listingsAgentDuck;
